import React, { useState, useRef, useEffect } from 'react';
import brandTheme from '../../theme/brandColors';
import { 
  uploadSignatureToS3, 
  retrieveSignatureFromS3, 
  deleteSignatureFromS3,
  isSignatureAPIConfigured 
} from '../../services/signatureS3Service';

// Destructure theme for easier access
const { colors, components, spacing, borderRadius, typography } = brandTheme;

// Signature Library Component - Manages saved signatures
const SignatureLibrary = ({ onUseSignature, username, currentSignature, onSaveSignature }) => {
  const [savedSignatures, setSavedSignatures] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const fileInputRef = useRef(null);

  // Load signatures from both localStorage and S3 when library is opened
  const loadAllSignatures = async () => {
    if (!username) {
      setIsLoadingLibrary(false);
      return;
    }

    setIsLoadingLibrary(true);
    let allSignatures = [];

    try {
      // 1. Load from localStorage
      const storageKey = `savedSignatures_${username}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          allSignatures = JSON.parse(stored);
        } catch (e) {
          console.error('Error loading saved signatures:', e);
          // Continue with empty array if parse fails
        }
      }

      // 2. Load from S3 if configured
      if (isSignatureAPIConfigured()) {
        try {
          const result = await retrieveSignatureFromS3(username, 1, true);
          if (result.success && result.signatureData) {
            // Check if S3 signature already exists in local storage
            const s3SignatureExists = allSignatures.some(sig => sig.source === 's3');

            if (!s3SignatureExists) {
              // Add S3 signature to the list
              const s3Signature = {
                id: 's3-signature',
                name: 'Cloud Signature',
                dataURL: result.signatureData,
                source: 's3',
                createdAt: new Date().toISOString()
              };
              allSignatures.unshift(s3Signature); // Add to beginning
            } else {
              // Update existing S3 signature
              allSignatures = allSignatures.map(sig =>
                sig.source === 's3'
                  ? { ...sig, dataURL: result.signatureData, updatedAt: new Date().toISOString() }
                  : sig
              );
            }
          }
        } catch (error) {
          console.log('[Signature] No S3 signature found or load failed');
          // Continue without S3 signature - user can still use local signatures
        }
      }

      setSavedSignatures(allSignatures);
    } catch (error) {
      console.error('[Signature] Error loading signatures:', error);
      // Ensure we set empty array even if everything fails
      setSavedSignatures([]);
    } finally {
      setIsLoadingLibrary(false);
    }
  };

  // Reload signatures when currentSignature changes (user saved a new one)
  useEffect(() => {
    if (showLibrary && username) {
      loadAllSignatures().catch(error => {
        console.error('[Signature] Failed to load signatures:', error);
        // Don't block the UI - library will show empty state
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLibrary, username, currentSignature]);

  // Save signatures to localStorage
  const saveToStorage = (signatures) => {
    if (!username) {
      alert('Please log in to save signatures');
      return;
    }
    try {
      const storageKey = `savedSignatures_${username}`;
      localStorage.setItem(storageKey, JSON.stringify(signatures));
      setSavedSignatures(signatures);
    } catch (e) {
      console.error('Error saving signatures:', e);
      alert('Failed to save signature to storage');
    }
  };

  // Normalize background to pure white while preserving signature
  const normalizeBackground = (ctx, width, height) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    // First pass: Calculate average background brightness
    let totalBrightness = 0;
    let pixelCount = 0;
    const brightnessThreshold = 200; // Pixels brighter than this are likely background
    
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = (r + g + b) / 3;
      
      if (brightness > brightnessThreshold) {
        totalBrightness += brightness;
        pixelCount++;
      }
    }
    
    const avgBackgroundBrightness = pixelCount > 0 ? totalBrightness / pixelCount : 255;
    
    // Second pass: Normalize background to white and enhance contrast
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
  // alpha channel not used directly, omit variable to reduce eslint warnings
  // const a = data[i + 3];
      const brightness = (r + g + b) / 3;
      
      // If pixel is close to background brightness, make it pure white
      if (brightness > avgBackgroundBrightness * 0.85) {
        data[i] = 255;     // R
        data[i + 1] = 255; // G
        data[i + 2] = 255; // B
      } else {
        // Enhance contrast for signature pixels
        const contrastFactor = 1.2;
        data[i] = Math.min(255, Math.max(0, (r - 128) * contrastFactor + 128));
        data[i + 1] = Math.min(255, Math.max(0, (g - 128) * contrastFactor + 128));
        data[i + 2] = Math.min(255, Math.max(0, (b - 128) * contrastFactor + 128));
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  };

  // Handle image file import
  const handleImageImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image too large. Please use an image smaller than 2MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Create canvas to resize/normalize the image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set standard signature size
          const maxWidth = 400;
          const maxHeight = 200;
          let width = img.width;
          let height = img.height;

          // Scale to fit within max dimensions
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          canvas.width = maxWidth;
          canvas.height = maxHeight;

          // Fill white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, maxWidth, maxHeight);

          // Center the image
          const x = (maxWidth - width) / 2;
          const y = (maxHeight - height) / 2;
          ctx.drawImage(img, x, y, width, height);

          // Normalize background to pure white
          normalizeBackground(ctx, maxWidth, maxHeight);

          // Convert to data URL
          const dataURL = canvas.toDataURL('image/png', 1.0);

          // Add to saved signatures
          const newSignature = {
            id: Date.now(),
            name: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
            dataURL: dataURL,
            createdAt: new Date().toISOString()
          };

          const updated = [...savedSignatures, newSignature];
          saveToStorage(updated);

          alert('Signature imported and saved successfully!');
        } catch (error) {
          console.error('Error processing image:', error);
          alert('Failed to process image. Please try a different image.');
        }
      };

      img.onerror = () => {
        alert('Failed to load image. Please try a different file.');
      };

      img.src = event.target.result;
    };

    reader.onerror = () => {
      alert('Failed to read file. Please try again.');
    };

    reader.readAsDataURL(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Delete a saved signature
  const handleDelete = async (signature) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this saved signature?')) {
      return;
    }

    // If it's an S3 signature, delete from cloud
    if (signature.source === 's3') {
      try {
        await deleteSignatureFromS3(username);
        alert('Cloud signature deleted successfully');
      } catch (error) {
        console.error('Error deleting from S3:', error);
        alert('Failed to delete cloud signature');
        return;
      }
    }

    // Remove from local state and storage
    const updated = savedSignatures.filter(sig => sig.id !== signature.id);
    saveToStorage(updated);
    setSavedSignatures(updated);

    // Clear default if it was the deleted signature
    if (username) {
      const defaultKey = `defaultSignature_${username}`;
      const currentDefault = localStorage.getItem(defaultKey);
      if (currentDefault === signature.id) {
        localStorage.removeItem(defaultKey);
      }
    }
  };

  // Set signature as default
  const handleSetDefault = (id) => {
    if (!username) {
      alert('Please log in to set a default signature');
      return;
    }
    const defaultKey = `defaultSignature_${username}`;
    localStorage.setItem(defaultKey, id);
    alert('Default signature set successfully!');
  };

  // Get default signature ID
  const getDefaultSignatureId = () => {
    if (!username) return null;
    const defaultKey = `defaultSignature_${username}`;
    return localStorage.getItem(defaultKey);
  };

  const defaultSignatureId = getDefaultSignatureId();

  // Use a saved signature
  const handleUse = (signature) => {
    onUseSignature(signature.dataURL);
    setShowLibrary(false);
  };

  // Open library and load all signatures
  const handleOpenLibrary = () => {
    setShowLibrary(true);
    // loadAllSignatures will be called by the useEffect
  };

  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{
        display: 'flex',
        gap: spacing.sm,
        marginBottom: spacing.md
      }}>
        {/* Save Current Signature Button */}
        {currentSignature && (
          <button
            type="button"
            onClick={onSaveSignature}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              backgroundColor: colors.primary.navy,
              color: 'white',
              border: `2px solid ${colors.primary.yellow}`,
              borderRadius: borderRadius.md,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.bold,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              boxShadow: `0 0 0 2px ${colors.primary.yellow}`
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = colors.primary.yellow;
              e.target.style.color = colors.primary.navy;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = `0 4px 8px rgba(244, 196, 48, 0.3), 0 0 0 2px ${colors.primary.yellow}`;
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = colors.primary.navy;
              e.target.style.color = 'white';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 0 0 2px ${colors.primary.yellow}`;
            }}
          >
            💾 Save Signature
          </button>
        )}

        {/* Signature Library Button */}
        <button
          type="button"
          onClick={handleOpenLibrary}
          disabled={isLoadingLibrary}
          style={{
            padding: `${spacing.sm} ${spacing.md}`,
            backgroundColor: '#8b5cf6',
            color: 'white',
            border: 'none',
            borderRadius: borderRadius.md,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            cursor: isLoadingLibrary ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            opacity: isLoadingLibrary ? 0.7 : 1
          }}
          onMouseOver={(e) => {
            if (!isLoadingLibrary) {
              e.target.style.backgroundColor = '#7c3aed';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
            }
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#8b5cf6';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
          }}
        >
          {isLoadingLibrary ? '⏳ Loading...' : '📁 Signature Library'}
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageImport}
        style={{ display: 'none' }}
      />

      {/* Signature Library Modal */}
      {showLibrary && (
        <div style={{
          border: `1px solid ${components.card.border}`,
          borderRadius: borderRadius.lg,
          padding: spacing.lg,
          background: components.header.backgroundGradient,
          marginBottom: spacing.lg,
          boxShadow: components.card.shadow
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: spacing.lg,
            paddingBottom: spacing.md,
            borderBottom: `2px solid ${components.signatureLibrary.headerText}`
          }}>
            <h4 style={{
              margin: 0,
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.semibold,
              color: components.signatureLibrary.headerText,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm
            }}>
              <span style={{ fontSize: typography.fontSize.xl }}>🖼️</span>
              Your Saved Signatures
            </h4>
            <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: borderRadius.md,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#059669';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#10b981';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                📤 Import Photo
              </button>
              <button
                onClick={() => setShowLibrary(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: typography.fontSize['2xl'],
                  cursor: 'pointer',
                  color: components.signatureLibrary.headerText,
                  padding: spacing.xs,
                  fontWeight: typography.fontWeight.bold,
                  transition: 'transform 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.2)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                ×
              </button>
            </div>
          </div>

          {savedSignatures.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: `${spacing.xl} ${spacing.md}`,
              color: colors.gray[600]
            }}>
              <div style={{ fontSize: typography.fontSize['3xl'], marginBottom: spacing.md }}>📝</div>
              <p style={{ fontSize: typography.fontSize.base, marginBottom: spacing.sm }}>
                No signatures saved yet
              </p>
              <p style={{ fontSize: typography.fontSize.sm, color: colors.gray[500] }}>
                Draw a signature and click "Save Signature" to store it here
              </p>
            </div>
          ) : null}


          {savedSignatures.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: spacing.md
            }}>
              {savedSignatures.map((sig) => (
              <div
                key={sig.id}
                style={{
                  border: `2px solid ${colors.gray[200]}`,
                  borderRadius: borderRadius.lg,
                  padding: spacing.md,
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.secondary.tan;
                  e.currentTarget.style.boxShadow = `0 4px 12px rgba(155, 146, 107, 0.2)`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.gray[200];
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Signature Preview */}
                <div style={{
                  backgroundColor: colors.gray[50],
                  border: `1px solid ${colors.gray[200]}`,
                  borderRadius: borderRadius.md,
                  padding: spacing.sm,
                  marginBottom: spacing.sm,
                  height: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  <img
                    src={sig.dataURL}
                    alt={sig.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>

                {/* Signature Name */}
                <div style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  color: colors.gray[700],
                  marginBottom: spacing.sm,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  flexWrap: 'wrap'
                }}>
                  {sig.name}
                  {sig.source === 's3' && (
                    <span style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: typography.fontWeight.bold,
                      padding: '2px 6px',
                      borderRadius: borderRadius.sm,
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      ☁️ Cloud
                    </span>
                  )}
                  {sig.id === defaultSignatureId && (
                    <span style={{
                      backgroundColor: colors.primary.yellow,
                      color: colors.primary.navy,
                      fontSize: '10px',
                      fontWeight: typography.fontWeight.bold,
                      padding: '2px 6px',
                      borderRadius: borderRadius.sm,
                      textTransform: 'uppercase'
                    }}>
                      Default
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.xs
                }}>
                  <div style={{ display: 'flex', gap: spacing.sm }}>
                    <button
                      onClick={() => handleUse(sig)}
                      style={{
                        flex: 1,
                        padding: `${spacing.sm} ${spacing.md}`,
                        backgroundColor: '#8b5cf6',  // Original purple
                        color: 'white',
                        border: 'none',
                        borderRadius: borderRadius.md,
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.semibold,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#7c3aed';  // Original purple hover
                        e.target.style.transform = 'scale(1.02)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#8b5cf6';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      ✓ Use
                    </button>
                    {sig.source !== 's3' && (
                      <button
                        onClick={() => handleDelete(sig)}
                        style={{
                          padding: `${spacing.sm} ${spacing.md}`,
                          backgroundColor: components.signatureLibrary.deleteButton,
                          color: 'white',
                          border: 'none',
                          borderRadius: borderRadius.md,
                          fontSize: typography.fontSize.sm,
                          fontWeight: typography.fontWeight.medium,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = components.signatureLibrary.deleteButtonHover;
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = components.signatureLibrary.deleteButton;
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                  {/* Set Default Button - Only for non-cloud signatures */}
                  {sig.id !== defaultSignatureId && sig.source !== 's3' && (
                  <button
                    onClick={() => handleSetDefault(sig.id)}
                    style={{
                      width: '100%',
                      padding: `${spacing.xs} ${spacing.sm}`,
                      backgroundColor: colors.primary.yellow,
                      color: colors.primary.navy,
                      border: `1px solid ${colors.primary.navy}`,
                      borderRadius: borderRadius.md,
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.bold,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = colors.primary.navy;
                      e.target.style.color = 'white';
                      e.target.style.transform = 'scale(1.02)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = colors.primary.yellow;
                      e.target.style.color = colors.primary.navy;
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    ⭐ Set as Default
                  </button>
                )}
              </div>
            </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Enhanced Signature Canvas Component with Smooth Bezier Curves
const SignatureCanvas = React.forwardRef(({ onSave, onClear, signature }, ref) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Store points for smooth curve drawing
  const pointsRef = useRef([]);
  const lastPointRef = useRef(null);
  const lastVelocityRef = useRef(0);
  const lastWidthRef = useRef(2);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });
    
    // Set canvas size with high DPI support for crisp rendering
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    ctx.scale(dpr, dpr);
    
    // Enhanced drawing styles for smooth, beautiful signatures
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2.5; // Slightly thicker default for better visibility
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // If there's an existing signature, draw it
    if (signature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = signature;
      setIsEmpty(false);
    }
  }, [signature]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      // Touch event
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
        time: Date.now()
      };
    } else {
      // Mouse event
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        time: Date.now()
      };
    }
  };

  // Calculate distance between two points
  const getDistance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  // Calculate velocity-based line width for pressure simulation
  const getLineWidth = (velocity) => {
    const minWidth = 1.5;
    const maxWidth = 3.5;
    const velocityFilterWeight = 0.7;
    
    // Smooth velocity changes
    const filteredVelocity = 
      velocityFilterWeight * velocity + 
      (1 - velocityFilterWeight) * lastVelocityRef.current;
    
    lastVelocityRef.current = filteredVelocity;
    
    // Map velocity to line width (faster = thinner, slower = thicker)
    const targetWidth = Math.max(minWidth, Math.min(maxWidth, maxWidth - (filteredVelocity * 0.05)));
    
    // Smooth width transitions
    const smoothedWidth = lastWidthRef.current * 0.7 + targetWidth * 0.3;
    lastWidthRef.current = smoothedWidth;
    
    return smoothedWidth;
  };

  // Draw smooth bezier curve between points
  const drawSmoothLine = (points) => {
    if (points.length < 2) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    if (points.length === 2) {
      // Just two points, draw a simple line
      const p1 = points[0];
      const p2 = points[1];
      
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.lineWidth = p1.width || 2.5;
      ctx.stroke();
    } else {
      // Draw smooth bezier curve through multiple points
      for (let i = 1; i < points.length - 1; i++) {
        const p0 = points[i - 1];
        const p1 = points[i];
        const p2 = points[i + 1];
        
        // Calculate control points for quadratic bezier curve
        const cp1x = (p0.x + p1.x) / 2;
        const cp1y = (p0.y + p1.y) / 2;
        const cp2x = (p1.x + p2.x) / 2;
        const cp2y = (p1.y + p2.y) / 2;
        
        ctx.beginPath();
        ctx.moveTo(cp1x, cp1y);
        ctx.quadraticCurveTo(p1.x, p1.y, cp2x, cp2y);
        ctx.lineWidth = p1.width || 2.5;
        ctx.stroke();
      }
    }
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCoordinates(e);
    
    pointsRef.current = [{
      x: coords.x,
      y: coords.y,
      time: coords.time,
      width: 2.5
    }];
    
    lastPointRef.current = coords;
    lastVelocityRef.current = 0;
    lastWidthRef.current = 2.5;
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    
    const coords = getCoordinates(e);
    const lastPoint = lastPointRef.current;
    
    if (!lastPoint) return;
    
    // Calculate velocity for pressure simulation
    const distance = getDistance(lastPoint, coords);
    const timeDelta = coords.time - lastPoint.time;
    const velocity = timeDelta > 0 ? distance / timeDelta : 0;
    
    // Get pressure-based line width
    const width = getLineWidth(velocity);
    
    // Add point with calculated width
    const newPoint = {
      x: coords.x,
      y: coords.y,
      time: coords.time,
      width: width
    };
    
    pointsRef.current.push(newPoint);
    
    // Draw smooth line through recent points
    const recentPoints = pointsRef.current.slice(-4); // Use last 4 points for smoothness
    drawSmoothLine(recentPoints);
    
    lastPointRef.current = coords;
    setIsEmpty(false);
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);
    
    // Draw final smoothing if we have points
    if (pointsRef.current.length > 1) {
      drawSmoothLine(pointsRef.current.slice(-3));
    }
    
    // Save signature
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png', 1.0);
    // Debug: log that we produced a dataURL from the canvas
    try {
      // Log only a short prefix to avoid huge console output
      // eslint-disable-next-line no-console
      console.log('[SignatureCanvas] stopDrawing -> produced dataURL length=', dataURL ? dataURL.length : 0);
    } catch (e) {}
    onSave(dataURL);
    
    // Clear points for next stroke
    pointsRef.current = [];
    lastPointRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setIsEmpty(true);

    // Reset drawing state
    pointsRef.current = [];
    lastPointRef.current = null;
    lastVelocityRef.current = 0;
    lastWidthRef.current = 2.5;
  };

  const handleClearClick = () => {
    // Clear the visual canvas
    clearCanvas();

    // Notify parent to clear form state
    if (onClear) {
      onClear();
    }
  };

  /**
   * Polish/Smooth an existing signature using advanced image processing
   * This applies post-processing to reduce noise and improve appearance
   */
  const polishSignature = () => {
    if (isEmpty || !signature) return;
    
    setIsProcessing(true);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Create temporary canvas for processing
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    
    // Copy current signature to temp canvas
    const img = new Image();
    img.onload = () => {
      // Draw original to temp canvas
      tempCtx.drawImage(img, 0, 0, rect.width, rect.height);
      
      // Get image data for processing
      const imageData = tempCtx.getImageData(0, 0, rect.width, rect.height);
      
      // Apply smoothing filter (bilateral-like filter to preserve edges while smoothing)
      const smoothed = applySmoothing(imageData, rect.width, rect.height);
      
      // Apply slight thickening to make signature bolder
      const thickened = applyThickening(smoothed, rect.width, rect.height);
      
      // Clear main canvas and draw processed image
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      tempCtx.putImageData(thickened, 0, 0);
      ctx.drawImage(tempCanvas, 0, 0);
      
      // Save polished signature
      const polishedDataURL = canvas.toDataURL('image/png', 1.0);
      onSave(polishedDataURL);
      
      setIsProcessing(false);
    };
    
    img.src = signature;
  };

  /**
   * Apply Gaussian-like smoothing to reduce jaggedness
   */
  const applySmoothing = (imageData, width, height) => {
    const data = imageData.data;
    const smoothedData = new ImageData(width, height);
    const output = smoothedData.data;
    
    // Simple 3x3 Gaussian kernel for smoothing
    const kernel = [
      1, 2, 1,
      2, 4, 2,
      1, 2, 1
    ];
    const kernelSum = 16;
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        // Skip white pixels (background)
        if (data[idx] > 240 && data[idx + 1] > 240 && data[idx + 2] > 240) {
          output[idx] = data[idx];
          output[idx + 1] = data[idx + 1];
          output[idx + 2] = data[idx + 2];
          output[idx + 3] = data[idx + 3];
          continue;
        }
        
        let r = 0, g = 0, b = 0, a = 0;
        let ki = 0;
        
        // Apply convolution
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const pixelIdx = ((y + ky) * width + (x + kx)) * 4;
            const weight = kernel[ki++];
            
            r += data[pixelIdx] * weight;
            g += data[pixelIdx + 1] * weight;
            b += data[pixelIdx + 2] * weight;
            a += data[pixelIdx + 3] * weight;
          }
        }
        
        output[idx] = r / kernelSum;
        output[idx + 1] = g / kernelSum;
        output[idx + 2] = b / kernelSum;
        output[idx + 3] = a / kernelSum;
      }
    }
    
    return smoothedData;
  };

  /**
   * Apply slight thickening/dilation to make signature bolder
   */
  const applyThickening = (imageData, width, height) => {
    const data = imageData.data;
    const thickenedData = new ImageData(width, height);
    const output = thickenedData.data;
    
    // Copy original data first
    for (let i = 0; i < data.length; i++) {
      output[i] = data[i];
    }
    
    // Apply morphological dilation (makes dark areas slightly thicker)
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        // If current pixel is not too dark, check neighbors
        if (data[idx] > 128) continue;
        
        // Check 4-connected neighbors
        const neighbors = [
          ((y - 1) * width + x) * 4,     // top
          ((y + 1) * width + x) * 4,     // bottom
          (y * width + (x - 1)) * 4,     // left
          (y * width + (x + 1)) * 4      // right
        ];
        
        // Find darkest neighbor
        let darkest = 255;
        for (const nIdx of neighbors) {
          if (data[nIdx] < darkest) {
            darkest = data[nIdx];
          }
        }
        
        // Apply slight darkening effect
        if (darkest < 200) {
          output[idx] = Math.min(output[idx], darkest + 30);
          output[idx + 1] = Math.min(output[idx + 1], darkest + 30);
          output[idx + 2] = Math.min(output[idx + 2], darkest + 30);
        }
      }
    }
    
    return thickenedData;
  };

  /**
   * Load an imported signature image onto the canvas
   */
  const loadImportedSignature = (imageDataURL) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const img = new Image();
    img.onload = () => {
      // Clear canvas
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      // Draw imported signature
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
      
      // Save to form
      const dataURL = canvas.toDataURL('image/png', 1.0);
      onSave(dataURL);
      setIsEmpty(false);
    };
    
    img.src = imageDataURL;
  };

  // Expose method to parent component
  React.useImperativeHandle(ref, () => ({
    loadImportedSignature,
    clearCanvas,
    // Return current canvas data URL (or null if empty)
    getDataURL: () => {
      try {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        // Use toDataURL on the rendered canvas
        return canvas.toDataURL('image/png', 1.0);
      } catch (e) {
        return null;
      }
    }
  }));

  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{ position: "relative" }}>
        <div style={{
          position: "absolute",
          top: "-0.6rem",
          left: spacing.md,
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.medium,
          color: colors.gray[700],
          backgroundColor: "white",
          padding: `0 ${spacing.xs}`,
          zIndex: 1
        }}>
          Signature <span style={{ color: colors.secondary.yellow, fontWeight: typography.fontWeight.bold }}>*</span>
        </div>
        <div style={{
          border: `2px solid ${colors.gray[200]}`,
          borderRadius: borderRadius.lg,
          padding: spacing.lg,
          backgroundColor: "white",
          boxShadow: components.card.shadow
        }}>
          <canvas
            ref={canvasRef}
            style={{
              border: `2px dashed ${colors.gray[300]}`,
              borderRadius: borderRadius.md,
              cursor: 'crosshair',
              display: 'block',
              width: '100%',
              maxWidth: '400px',
              height: '200px',
              touchAction: 'none',
              backgroundColor: '#ffffff',
              transition: 'border-color 0.2s ease'
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onMouseEnter={(e) => e.target.style.borderColor = colors.primary.navy}
            onMouseOut={(e) => e.target.style.borderColor = colors.gray[300]}
          />
          <div style={{
            marginTop: spacing.md,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.gray[600],
              flex: 1,
              fontStyle: 'italic'
            }}>
              ✍️ Sign naturally - smoothing enabled for beautiful signatures
            </div>
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <button
                type="button"
                onClick={polishSignature}
                disabled={isEmpty || isProcessing}
                style={{
                  padding: `${spacing.sm} ${spacing.md}`,
                  backgroundColor: isEmpty || isProcessing ? colors.gray[300] : '#3b82f6',  // Original blue
                  color: isEmpty || isProcessing ? colors.gray[500] : 'white',
                  border: 'none',
                  borderRadius: borderRadius.md,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  cursor: isEmpty || isProcessing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  boxShadow: isEmpty || isProcessing ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
                onMouseOver={(e) => {
                  if (!isEmpty && !isProcessing) {
                    e.target.style.backgroundColor = '#2563eb';  // Original blue hover
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isEmpty && !isProcessing) {
                    e.target.style.backgroundColor = '#3b82f6';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                {isProcessing ? '✨ Processing...' : '✨ Polish'}
              </button>
              <button
                type="button"
                onClick={handleClearClick}
                style={{
                  padding: `${spacing.sm} ${spacing.md}`,
                  backgroundColor: 'white',
                  color: colors.gray[700],
                  border: `1px solid ${colors.gray[300]}`,
                  borderRadius: borderRadius.md,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = colors.gray[50];
                  e.target.style.borderColor = colors.gray[400];
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = colors.gray[300];
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const Step13 = React.forwardRef(({ 
  username,
  signoffData = {}, 
  handleSignoffChange = () => {},
  errors = {} 
}, ref) => {
  // Ref for SignatureCanvas to access loadImportedSignature method
  const signatureCanvasRef = useRef(null);

  // Expose helper for parent to read current signature data from the canvas/form
  React.useImperativeHandle(ref, () => ({
    getSignatureData: () => {
      // Prefer the saved form signature value
      try {
        if (formData && formData.signature) return formData.signature;
      } catch (e) {}
      // Fallback to reading the raw canvas if available
      try {
        return signatureCanvasRef.current && signatureCanvasRef.current.getDataURL ? signatureCanvasRef.current.getDataURL() : null;
      } catch (e) {
        return null;
      }
    }
  }));

  // Helper function to get current datetime in local format
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    priority: signoffData.priority || '',
    timeOfCompletion: signoffData.timeOfCompletion || getCurrentDateTime(),
    signature: signoffData.signature || ''
  });

  const priorityOptions = [
    { value: 'standard', label: 'Standard' },
    { value: 'immediate', label: 'Immediate' },
    { value: 'critical', label: 'Critical' }
  ];

  // Sync props to local state when signoffData changes (e.g., when navigating back to this step)
  useEffect(() => {
    setFormData({
      priority: signoffData.priority || '',
      timeOfCompletion: signoffData.timeOfCompletion || getCurrentDateTime(),
      signature: signoffData.signature || ''
    });
  }, [signoffData.priority, signoffData.timeOfCompletion, signoffData.signature]);

  useEffect(() => {
    // Initialize time of completion if not already set
    if (!formData.timeOfCompletion) {
      const updatedData = { ...formData, timeOfCompletion: getCurrentDateTime() };
      setFormData(updatedData);
      handleSignoffChange(updatedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load signature from S3 if available and form signature is empty
  useEffect(() => {
    const loadSignatureFromS3 = async () => {
      if (!username || !isSignatureAPIConfigured() || formData.signature) {
        return; // Skip if no username, API not configured, or signature already exists
      }

      try {
        console.log('[Signature] Auto-loading from S3...');
        const result = await retrieveSignatureFromS3(username, 1, true);

        if (result.success && result.signatureData) {
          // Load signature into canvas
          if (signatureCanvasRef.current && signatureCanvasRef.current.loadImportedSignature) {
            signatureCanvasRef.current.loadImportedSignature(result.signatureData);
            console.log('[Signature] Auto-loaded from S3 successfully');
          }
        }
      } catch (error) {
        console.log('[Signature] No S3 signature found or load failed (this is normal for first-time users)');
        // Don't block the form - user can still draw signature manually
      }
    };

    loadSignatureFromS3().catch(error => {
      console.error('[Signature] Auto-load error:', error);
      // Silently fail - user can still use the canvas
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]); // Only run when username changes

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    handleSignoffChange(updatedData);
  };

  const handleSignatureSave = async (signatureData) => {
    // Debug: log that Step13 received a signatureData to save
    try {
      // eslint-disable-next-line no-console
      console.log('[Step13] handleSignatureSave -> signatureData length=', signatureData ? signatureData.length : 0);
    } catch (e) {}

    // Save to form state FIRST before any async operations
    // This ensures the signature is available for validation immediately
    handleInputChange('signature', signatureData);

    // Auto-save to S3 if configured and user is logged in (background operation)
    // We don't await this to avoid blocking the UI
    if (username && signatureData && isSignatureAPIConfigured()) {
      uploadSignatureToS3(username, signatureData)
        .then(result => {
          if (result.success) {
            console.log('[Signature] Auto-saved to S3 successfully');
          }
        })
        .catch(error => {
          console.error('[Signature] Auto-save to S3 failed:', error);
          // Don't alert - this is background save, form still works
        });
    }
  };

  const clearSignature = () => {
    // Only clear form state - the canvas component handles its own clearing
    handleInputChange('signature', '');
  };

  const handleUseSignature = (dataURL) => {
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.loadImportedSignature(dataURL);
    }
  };

  const handleSaveSignature = async () => {
    if (!formData.signature) {
      alert('Please draw a signature first');
      return;
    }

    if (!username) {
      alert('Please log in to save signatures');
      return;
    }

    // 1. Save to localStorage
    const storageKey = `savedSignatures_${username}`;
    const stored = localStorage.getItem(storageKey);
    let signatures = [];

    try {
      if (stored) {
        signatures = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error loading saved signatures:', e);
    }

    const newSignature = {
      id: Date.now(),
      name: `Signature ${signatures.length + 1}`,
      dataURL: formData.signature,
      createdAt: new Date().toISOString()
    };

    signatures.push(newSignature);
    localStorage.setItem(storageKey, JSON.stringify(signatures));

    // 2. Save to S3 if configured (background operation)
    if (isSignatureAPIConfigured()) {
      try {
        await uploadSignatureToS3(username, formData.signature);
        alert('✅ Signature saved to library and cloud!\n\nYou can access it from any device.');
      } catch (error) {
        console.error('Error saving to S3:', error);
        // Still saved locally, so don't show error - just log it
        alert(`✅ Signature saved to local library!\n\nYou now have ${signatures.length} saved signature(s).\n\nNote: Cloud sync failed but your signature is saved locally.`);
      }
    } else {
      alert(`✅ Signature saved to library!\n\nYou now have ${signatures.length} saved signature(s).`);
    }
  };

  const getFieldStyle = (fieldName) => ({
    width: "100%",
    padding: `${spacing.md} ${spacing.md}`,
    backgroundColor: "white",
    border: `2px solid ${errors[fieldName] ? components.form.borderError : components.form.border}`,
    borderRadius: borderRadius.lg,
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    fontSize: typography.fontSize.base,
    color: components.form.text,
    outline: "none",
    transition: "all 0.2s ease",
    boxSizing: "border-box"
  });

  return (
    <div>
      {/* Priority */}
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ position: "relative" }}>
          <div style={{
            position: "absolute",
            top: "-0.6rem",
            left: spacing.md,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.gray[700],
            backgroundColor: "white",
            padding: `0 ${spacing.xs}`,
            zIndex: 1
          }}>
            Priority <span style={{ color: colors.secondary.yellow, fontWeight: typography.fontWeight.bold }}>*</span>
          </div>
          <select
            value={formData.priority}
            onChange={e => handleInputChange('priority', e.target.value)}
            style={{
              ...getFieldStyle('priority'),
              cursor: 'pointer',
              paddingRight: '2.5rem'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = colors.primary.navy;
              e.target.style.boxShadow = `0 0 0 3px ${components.form.focusRing}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = errors.priority ? components.form.borderError : components.form.border;
              e.target.style.boxShadow = "0 1px 3px 0 rgba(0, 0, 0, 0.1)";
            }}
          >
            <option value="">Select priority level</option>
            {priorityOptions.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.priority && (
            <div style={{ 
              color: components.form.borderError, 
              fontSize: typography.fontSize.sm, 
              marginTop: spacing.xs,
              fontWeight: typography.fontWeight.medium
            }}>
              {errors.priority}
            </div>
          )}
        </div>
      </div>

      {/* Time of Completion */}
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ position: "relative" }}>
          <div style={{
            position: "absolute",
            top: "-0.6rem",
            left: spacing.md,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.gray[700],
            backgroundColor: "white",
            padding: `0 ${spacing.xs}`,
            zIndex: 1
          }}>
            Time of completion <span style={{ color: colors.secondary.yellow, fontWeight: typography.fontWeight.bold }}>*</span>
          </div>
          <input
            type="datetime-local"
            value={formData.timeOfCompletion}
            onChange={e => handleInputChange('timeOfCompletion', e.target.value)}
            style={getFieldStyle('timeOfCompletion')}
            onFocus={(e) => {
              e.target.style.borderColor = colors.primary.navy;
              e.target.style.boxShadow = `0 0 0 3px ${components.form.focusRing}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = errors.timeOfCompletion ? components.form.borderError : components.form.border;
              e.target.style.boxShadow = "0 1px 3px 0 rgba(0, 0, 0, 0.1)";
            }}
          />
          {errors.timeOfCompletion && (
            <div style={{ 
              color: components.form.borderError, 
              fontSize: typography.fontSize.sm, 
              marginTop: spacing.xs,
              fontWeight: typography.fontWeight.medium
            }}>
              {errors.timeOfCompletion}
            </div>
          )}
        </div>
      </div>

      {/* Signature Library */}
      <SignatureLibrary
        onUseSignature={handleUseSignature}
        username={username}
        currentSignature={formData.signature}
        onSaveSignature={handleSaveSignature}
      />

      {/* Signature */}
      <SignatureCanvas
        ref={signatureCanvasRef}
        onSave={handleSignatureSave}
        onClear={clearSignature}
        signature={formData.signature}
      />
      {errors.signature && (
        <div style={{ 
          color: components.form.borderError, 
          fontSize: typography.fontSize.sm, 
          marginTop: spacing.sm, 
          marginBottom: spacing.md,
          fontWeight: typography.fontWeight.medium
        }}>
          {errors.signature}
        </div>
      )}
    </div>
  );
});

export default Step13;